import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { Col } from '@components/styles.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { getOverlayBounds, getOverlayZIndex } from '@utils/contentBounds.ts';
import { Z_LAYERS } from '@styles/zLayers.ts';
import SvgButtonTagEdit from '@components/framework/tag/ButtonTagEdit.tsx';
import { InputText } from 'primereact/inputtext';
import SvgButtonTagAdd from '@components/framework/tag/ButtonTagAdd.tsx';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import SvgButtonTagError from '@components/framework/tag/ButtonTagError.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';

interface ComponentProps {
  secured?: SecuredConfig;
  label?: string | React.ReactNode;
  value?: string | ReactNode;
  icon?: JSX.Element;
  actionIcon?: JSX.Element;
  actionPane?: React.ReactNode;
  onClose?: () => void;
  onSave?: (onSuccess: () => void) => void;
  /** Greys out the save button while the pane holds a value the tag cannot persist. */
  saveDisabled?: boolean;
  errorMessage?: string;
  hideButtons?: boolean;
  autoClose?: boolean;
  required?: boolean;
  readonly?: boolean;
  children?: ReactElement;
  popupWidth?: string;
  isLabelsButtonTag?: boolean;
  // Top-aligns the icon and the action icon instead of centering them — for tags whose value can grow
  // to many lines (notes), where a centered pencil floats in the middle of the block.
  alignTop?: boolean;
  className?: string;
  dataTestId?: string;
  dataTestIdValue?: string;
  dataTestIdSave?: string;
  dataTestIdClose?: string;
}

export const InputTextPane = <T,>({ value, onChange }: { value?: T; onChange?: (value: T) => void }) => {
  return (
    <>
      {/*// @ts-ignore*/}
      <InputText value={`${value}`} onChange={e => onChange(e.target.value)} />
    </>
  );
};

const ButtonTag = ({
  label,
  icon,
  actionIcon,
  actionPane,
  onClose = () => {},
  value,
  onSave = () => {},
  saveDisabled = false,
  hideButtons = false,
  autoClose = false,
  required = false,
  readonly = false,
  errorMessage,
  popupWidth,
  children,
  isLabelsButtonTag,
  alignTop = false,
  className,
  dataTestId,
  dataTestIdValue,
  dataTestIdSave,
  dataTestIdClose,
  secured,
}: ComponentProps) => {
  const decoration = useSecuredDecoration(secured);
  // When a security rule supplies editability, it is authoritative; otherwise fall back to the readonly prop.
  const effectiveReadonly = decoration.editable === null ? readonly : !decoration.editable;
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  /** Viewport coordinates and stacking for the portaled popup; null until it has been measured. */
  const [position, setPosition] = useState<{ top: number; left: number; zIndex: number } | null>(null);

  useEffect(() => {
    const updatePosition = () => {
      const summary = summaryRef.current;
      const popup = popupRef.current;
      if (!open || !summary || !popup) {
        setPosition(null);
        return;
      }

      const rect = summary.getBoundingClientRect();
      const popupHeight = popup.offsetHeight;
      const popupWidth = popup.offsetWidth;
      const margin = 8;

      // Above only when it genuinely fits there and does not fit below — otherwise below, which is
      // where the popup belongs by default.
      const placeAbove = popupHeight <= rect.top && popupHeight > window.innerHeight - rect.bottom;
      const top = placeAbove ? rect.top - popupHeight - margin : rect.bottom + margin;
      // Left-aligned to the tag, pulled back inside its band when the tag sits near the edge — the
      // content column, or the side panel the tag lives in.
      const bounds = getOverlayBounds(summary);
      const left = Math.max(bounds.left + margin, Math.min(rect.left, bounds.right - popupWidth - margin));

      // One step above the panel the tag lives in, when that panel has been pushed above the popup
      // layer (see getOverlayZIndex); the plain popup layer otherwise.
      setPosition({ top, left, zIndex: getOverlayZIndex(summary) });
    };

    const handleScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target instanceof Element) {
        if (popupRef.current && popupRef.current.contains(target)) return;
        if (
          target.closest('.p-multiselect-panel, .p-dropdown-panel, .p-autocomplete-panel, .p-datepicker, .p-overlaypanel, .p-tieredmenu, .p-contextmenu, .p-menu-overlay')
        )
          return;
      }
      updatePosition();
    };

    if (open) {
      requestAnimationFrame(updatePosition);
    }

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const handleClickOutside = (event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest('li.p-dropdown-item') ||
      target.closest('.p-dropdown-panel') ||
      target.closest('div.p-checkbox-box') ||
      target.closest('.p-multiselect-panel') ||
      target.closest('.p-datepicker') ||
      target.closest('.p-autocomplete-item') ||
      target.closest('[data-dropdown-list]')
    ) {
      return;
    }

    // The popup is portaled to <body>, so it is not a descendant of the details element — without
    // the second check every click inside the popup would read as a click outside and close it.
    const insideTag = detailsRef.current?.contains(target);
    const insidePopup = popupRef.current?.contains(target);
    if (!insideTag && !insidePopup) {
      setOpen(false);
      onClose();
    }
  };

  useEffect(() => {
    if (open && autoClose) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const variant = isNullOrUndefined(errorMessage) ? (isNullOrUndefined(value) || value === '' ? (required ? 'required' : 'empty') : 'defined') : 'error';
  // Only the populated variant can grow tall; the empty/required pill keeps its centered 48px layout.
  const topAligned = alignTop && variant === 'defined';

  return (
    <>
      {decoration.decorate(
        <Details
          ref={detailsRef}
          open={open}
          onClick={e => {
            e.preventDefault();
          }}
          className={className}
        >
          <summary
            ref={summaryRef}
            data-testid={dataTestId}
            onClick={e => {
              if (!effectiveReadonly) setOpen(!open);
            }}
            className={twMerge(
              clsx(
                'flex list-none rounded-lg px-3 py-1.5 gap-3 border border-solid border-zinc-600',
                topAligned ? 'items-start' : 'items-center content-center',
                {
                  'cursor-pointer': !effectiveReadonly,
                },
                {
                  'bg-zinc-200 font-medium': variant === 'empty',
                },
                {
                  'bg-complementary-warningMedium': variant === 'required' && !effectiveReadonly,
                },
                {
                  'bg-complementary-errorMedium border-complementary-error': variant === 'error',
                },
                {
                  'bg-white border-content-brandMedium': variant === 'defined',
                },
                isLabelsButtonTag && variant === 'empty' && 'border-zinc-600 bg-gray-200',
                isLabelsButtonTag && variant !== 'empty' && 'bg-white border-content-brandMedium',
                value ? 'h-full' : 'h-12',
              ),
            )}
          >
            {icon && isNullOrUndefined(errorMessage) && <div className={clsx('text-content-secondary', topAligned && 'py-2')}>{icon}</div>}

            {isNotNullOrUndefined(errorMessage) && (
              <div>
                <SvgButtonTagError />
              </div>
            )}

            {label && typeof label === 'string' && (
              // topAligned tags are fixed-width blocks, so the value column has to fill the row for the
              // action icon to sit at the right edge instead of hugging a short value.
              <Col className={clsx(topAligned && 'flex-1 min-w-0')}>
                {isNotNullOrUndefined(errorMessage) && <div className={'text-complementary-error'}>{errorMessage}</div>}
                <div className={twMerge(clsx('text-content-secondary text-xs leading-[1.33333]', variant === 'empty' && 'text-zinc-600 text-sm'))}>{label}</div> 
                {variant !== 'empty' && (
                  <div className={'font-medium text-sm leading-[1.28571429]'}>
                    {errorMessage ? (
                      <span className="text-complementary-error" data-testid={dataTestIdValue}>
                        {value}
                      </span>
                    ) : (
                      /* overflowWrap 'anywhere': free-text values (notes) can be one long unbroken word;
                         without it the tag grows past the panel it sits in instead of wrapping. */
                      <span data-testid={dataTestIdValue} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {value || <>&nbsp;</>}
                      </span>
                    )}
                  </div>
                )}
              </Col>
            )}

            {label && typeof label !== 'string' && <span>{label}</span>}

            {!effectiveReadonly && isNotNullOrUndefined(value) && (
              <div className={clsx('ml-1', topAligned && 'py-2')}>
                <SvgButtonTagEdit
                  onClick={e => {
                    setOpen(true);
                  }}
                >
                  {actionIcon}
                </SvgButtonTagEdit>
              </div>
            )}
            {!effectiveReadonly && isNullOrUndefined(value) && (
              <SvgButtonTagAdd
                onClick={e => {
                  setOpen(true);
                }}
              >
                {actionIcon}
              </SvgButtonTagAdd>
            )}
          </summary>
          {open &&
            // Portaled to <body> rather than rendered inside the tag: the shell's content column is a
            // scroller capped at 1550px, so an inline popup anchored near its right edge was clipped
            // instead of spilling over the region beside it. Placement is therefore explicit, in
            // viewport coordinates — the same arrangement LocationSelect and MoreButton use.
            createPortal(
              <div
                ref={popupRef}
                className={
                  'button-tag-dialog flex flex-col p-6 bg-base-secondary fixed rounded-lg shadow-[0px_8px_24px_rgba(0,0,0,0.1)] gap-4'
                }
                style={{
                  top: position?.top ?? 0,
                  left: position?.left ?? 0,
                  zIndex: position?.zIndex ?? Z_LAYERS.popup,
                  width: popupWidth,
                  maxWidth: 'calc(var(--app-content-width, 100vw) - 2rem)',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  // Hidden for the frame between mounting and being measured, so it is never seen at
                  // the top-left corner first.
                  visibility: position ? 'visible' : 'hidden',
                }}
              >
                {actionPane}
                {children}
                {!hideButtons && (
                  <TagPaneButtonRow>
                    <RingilButton
                      label={tr(`RingilSidebar.continue`, `Continue`)}
                      onClick={() => {
                        if (onSave !== null) {
                          onSave(() => setOpen(false));
                        } else {
                          setOpen(false);
                        }
                      }}
                      mode={'filled'}
                      size={'big'}
                      loading={false}
                      disabled={saveDisabled}
                      dataTestId={dataTestIdSave ?? (dataTestId ? `${dataTestId}Save` : undefined)}
                    />
                    <RingilButton
                      label={tr(`RingilSidebar.back`, `Back`)}
                      onClick={() => {
                        if (onClose !== null) onClose();
                        setOpen(false);
                      }}
                      mode={'text-only'}
                      size={'big'}
                      loading={false}
                      dataTestId={dataTestIdClose ?? (dataTestId ? `${dataTestId}Close` : undefined)}
                    />
                  </TagPaneButtonRow>
                )}
              </div>,
              document.body,
            )}
        </Details>,
      )}
    </>
  );
};

const TagPaneButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Details = styled.details`
  display: inline-block;
  position: relative;
  /* Keeps a tag with a long value inside the row it belongs to instead of widening it. */
  max-width: 100%;

  & [open] summary {
    border: 1px solid var(--content-brand);
    background-color: var(--base-basesecondary);
  }
`;

export default ButtonTag;
